import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { WatchOptions } from 'vue';
import InputMaskedText from '@/components/inputs/InputMaskedText/InputMaskedText';
import InputText from '@/components/inputs/InputText/InputText';
import InputCheckbox from '@/components/inputs/InputCheckbox/InputCheckbox';
import { MedicalDataDTO } from '@/models/MedicalDataDTO';
import InsuranceAutoComplete from '@/components/inputs/InsuranceAutoComplete/InsuranceAutoComplete';
import LocalityAutocomplete from '@/components/inputs/LocalityAutoComplete/LocalityAutoComplete';
import { McsisRole } from '@/models/constants/McsisRole';
import DoctorAutoComplete from '@/components/inputs/DoctorAutoComplete/DoctorAutoComplete';
import { DoctorDTO } from '@/models/DoctorDTO';
import DoctorService from '@/services/doctor.service';
import LocalityService from '@/services/locality.service';
import { InsuranceDTO } from '@/models/InsuranceDTO';
import InsuranceService from '@/services/insurance.service';

@Component({
  components: {
    InsuranceAutoComplete,
    InputText,
    InputMaskedText,
    InputCheckbox,
    LocalityAutocomplete,
    DoctorAutoComplete,
  },
})
export default class MedicalData extends Vue {
  @Prop() public value!: MedicalDataDTO;
  @Prop({ default: true }) public updating?: boolean;
  @Prop({ default: true }) public doctorUpdating?: boolean;
  @Prop({ default: false }) public readonly?: boolean;
  @Prop({ default: false }) public isRecap?: boolean;
  @Prop({ default: false }) public submitted!: boolean;
  @Prop({ default: false }) public avsErrorProp?: boolean;
  public partId: number | undefined;
  // Local mirror of avsErrorProp: handleFormValidation needs to update this
  // as the user types/blurs, and mutating a prop directly is not allowed in Vue.
  private avsError: boolean = false;
  public noDoctorInList: boolean = false;
  public doctorDefault: DoctorDTO = {
    doctorId: -10,
    doctorName: '---',
    doctorAddress: '',
    doctorAddressId: -10,
  } as DoctorDTO;
  public noDoctorDefault: DoctorDTO = {
    doctorId: -1,
    doctorName: '---',
    doctorAddress: '',
    doctorAddressId: -1,
  } as DoctorDTO;
  private medicalData: MedicalDataDTO = {} as MedicalDataDTO;
  private medicalDataDoctor: DoctorDTO | null = {} as DoctorDTO;
  private errorMessage?: string = undefined;
  private isDoctor: boolean = false;

  protected async mounted(): Promise<void> {
    this.avsError = !!this.avsErrorProp;

    // we make sure part id is set to be able to save medical data
    this.partId = Number(this.$route.params.partId);
    this.medicalData.partId = this.partId;

    this.medicalData = JSON.parse(JSON.stringify(this.value));

    if (this.medicalData.doctorId === null || this.medicalData.doctorId === undefined) {

      if (this.medicalData.doctorLastname &&
        ((this.noDoctorInList && !this.medicalData.noDoctor) || !this.noDoctorInList)) {
        this.onNoDoctorInList(true);
      }

      if (!this.medicalData.doctorLastname && this.medicalData.noDoctor) {
        this.onNoDoctor(true);
      }
    }

    if (this.medicalData.doctorId) {
      this.medicalDataDoctor = await DoctorService.getDoctor(this.medicalData.doctorId);
    }

    this.$isInRole(McsisRole.Doctor).then((isInRole: boolean) => {
      this.isDoctor = isInRole;
    });
  }

  /**
   * Returns true is we are in edit mode (handles case for a doctor).
   */
  protected isUpdating(): boolean {

    // Normal behabiour
    if (!this.isDoctor) {
      return !!this.updating;
    }

    // If it's a doctor, the fields are not editable by default
    // (a button let the doctor edit the data)
    return !!this.doctorUpdating;

  }

  /**
   * If checked, clears the doctor data
   * @param checked True if the checkbox is checked, false otherwise
   */
  protected onNoDoctor(checked: boolean) {
    this.medicalData.noDoctor = checked;

    // reinitialize data about the doctor
    if (checked) {
      this.medicalData.doctorLastname = null;
      this.medicalData.doctorFirstname = null;
      this.medicalData.doctorLocality = null;
      this.medicalData.doctorName = null;
      delete this.medicalData.doctorId;
    }

    // reinitialize data about the doctor
    this.medicalDataDoctor = checked ? this.noDoctorDefault : null;

    this.emitUpdateMedicalData();
  }

  /**
   * If checked, clears the doctor data
   * @param checked True if the checkbox is checked, false otherwise
   */
   protected onNoDoctorInList(checked: boolean) {
    this.noDoctorInList = checked;

    // reinitialize data about the doctor
    this.medicalDataDoctor = checked ? this.doctorDefault : null;

    // In case we have a doctorName, it means that we are loading data from the API, so don't clear the fields
    if (!this.medicalData.doctorName) {
      this.medicalData.doctorFirstname = null;
      this.medicalData.doctorLastname = null;
      this.medicalData.doctorLocality = null;
      this.medicalData.doctorName = null;
      delete this.medicalData.doctorId;
    }

    this.emitUpdateMedicalData();
  }

  protected async onDoctorSelected(value: DoctorDTO | undefined): Promise<void> {
    // In case the autocomplete is emptied, we must clear the data
    if (value === undefined) {
      this.medicalData.doctorId = undefined;
      this.medicalData.doctorLocality = null;
      this.emitUpdateMedicalData();
      return;
    }

    if (value && value.doctorLocId !== undefined) {
      if (value.doctorId > 0) {
        const doctorLocality = await LocalityService.fetchLocality(value.doctorLocId);
        this.medicalData.doctorId = value.doctorId;
        this.medicalData.doctorLocality = doctorLocality;
      } else {
        // In case there one on the checkboxes are checked (no doctor or no doctor in list), we clear the data
        this.medicalData.doctorId = undefined;
        this.medicalData.doctorLocality = null;
      }
      this.emitUpdateMedicalData();
    }
  }

  protected async onInsuranceSelected(value: InsuranceDTO | undefined): Promise<void> {
    // In case the autocomplete is emptied, we must clear the data
    if (value === undefined) {
      this.medicalData.insurance = null;
      this.emitUpdateMedicalData();
      return;
    }

    if (value && value.personId !== undefined) {
      if (value.personId > 0) {
        this.medicalData.insurance = value;
      } else {
        // In case there one on the checkboxes are checked (no doctor or no doctor in list), we clear the data
        this.medicalData.insurance = null;
      }
      this.emitUpdateMedicalData();
    }
  }

  /**
   * If checked, clears the insurance data
   * @param checked True if the checkbox is checked, false otherwise
   */
  protected onNoInsuranceInList(checked: boolean) {
    this.medicalData.noInsuranceInList = checked;

    // reinitialize data about the insurance
    if (checked) {
      this.medicalData.insurance = null;
    }
    this.emitUpdateMedicalData();

  }

  /**
   * Handles the validity of the AVS field
   * @param v True is the field is valid (match the mask config).
   */
  protected handleFormValidation(v: boolean): void {
    if (v && !!this.medicalData.avsNumber && !this.medicalData.avsNumber.startsWith('756') ) {
      this.$emit('data-valid', false);
      this.avsError = true;
      return;
    }

    this.$emit('data-valid', v);
    this.avsError = !v && !!this.medicalData.avsNumber;
  }

  /**
   * Emit medical data to parent MedicalContent.
   */
  protected emitUpdateMedicalData() {
    this.$emit('update-medical-data', this.medicalData);
  }

  @Watch('value', { deep: true } as WatchOptions)
  protected async onValueChanged(value: MedicalDataDTO, oldValue: MedicalDataDTO): Promise<void> {
    if (oldValue !== value) {
      this.medicalData = JSON.parse(JSON.stringify(this.value));

      if (!!oldValue && !!value) {
        if ((value.doctorId !== undefined && value.doctorId !== null) &&
            value.doctorId > 0) {
              this.medicalDataDoctor = await DoctorService.getDoctor(value.doctorId);
              return;
        }

        if ((!!oldValue.doctorFirstname && oldValue.doctorFirstname !== value.doctorFirstname) ||
          (!!oldValue.doctorLastname && oldValue.doctorLastname !== value.doctorLastname) ||
          (!!oldValue.doctorLocality && !!value.doctorLocality &&
            oldValue.doctorLocality.locId !== value.doctorLocality.locId)) {
            this.medicalData.doctorId = undefined;
        }
      }
    }
  }
}
